import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { faTrash, faRepeat } from '@fortawesome/free-solid-svg-icons';
import {
  useNotifications,
  StatusBadge,
  createTextColumn,
  createDateColumn,
  createCustomColumn,
  useTableState,
  useColumnSettings,
  ActionButton,
} from '@medprompt/shared';
import { ConsultationStatusEnum } from '../../enums/consultationStatusEnum';
import MicrophoneCheckService from '../../services/MicrophoneCheckService';
import { saveConsultationNavigation } from '../consultationNavigationSession';

/**
 * Columns, table state, selection and bulk actions for the scribe table, plus the two ends of
 * detail navigation: opening a row with its prev/next context, and highlighting the row on return.
 */
const useScribeTable = ({ list, patientNames, rowActions }) => {
  const { consultationService, refreshConsultations, userIdToClinicianData } = list;
  const { nhsIdToPatientData } = patientNames;
  const { getRowActions, handleRegenerateClick, handleDeleteClick } = rowActions;
  const { addNotification } = useNotifications();
  const navigate = useNavigate();
  const location = useLocation();

  const {
    sortState,
    setSortState,
    filterValues,
    setFilterValues,
    disabledFilters,
    setDisabledFilters,
    paginationState,
    setPaginationState,
    highlightedRows,
    setHighlightedRows
  } = useTableState('ScribeListTable', {
    defaultSortState: { columnKey: 'timestamp', direction: 'desc' }
  });
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [columnCustomizationOpen, setColumnCustomizationOpen] = useState(false);
  const [bulkDeleteModalOpen, setBulkDeleteModalOpen] = useState(false);
  const [bulkRegenerateModalOpen, setBulkRegenerateModalOpen] = useState(false);
  const [consultationsForBulkAction, setConsultationsForBulkAction] = useState([]);
  const [bulkRegenerateLoading, setBulkRegenerateLoading] = useState(false);

  // Returning from the consultation detail page lands here with the consultation to highlight
  useEffect(() => {
    const highlightConsultationId = location.state?.highlightConsultationId;
    if (highlightConsultationId) {
      setHighlightedRows(new Set([highlightConsultationId]));
      // Clear the location state to prevent re-highlighting on subsequent renders
      window.history.replaceState({}, document.title);
    }
  }, [location.state, setHighlightedRows]);

  const getPatientName = useCallback((nhsId) => {
    if (!nhsId) return 'Unknown';
    if (nhsIdToPatientData.has(nhsId)) {
      const patientData = nhsIdToPatientData.get(nhsId);
      return patientData?.name || patientData?.fullName || nhsId;
    }
    return nhsId;
  }, [nhsIdToPatientData]);

  // The table's current filtered and sorted row order, which is what prev/next pages through
  const filteredConsultationIdsRef = useRef([]);
  const handleFilteredSortedDataChange = useCallback((filteredSortedConsultations) => {
    if (!Array.isArray(filteredSortedConsultations)) return;
    filteredConsultationIdsRef.current = filteredSortedConsultations.map(c => c.consultationId);
  }, []);

  // Opening a RECORDING row waits on a microphone check (which can sit behind a permission
  // prompt). Only the latest click, made while the list is still mounted, may navigate.
  const openRequestIdRef = useRef(0);
  const isMountedRef = useRef(false);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const openConsultation = useCallback(async (consultation) => {
    const requestId = ++openRequestIdRef.current;

    if (consultation.status === ConsultationStatusEnum.RECORDING.value) {
      const micStatus = await MicrophoneCheckService.checkMicrophoneStatus();
      if (requestId !== openRequestIdRef.current || !isMountedRef.current) return;

      if (!micStatus.canRecord) {
        addNotification('mic-check-failed', micStatus.message, { isError: true });
        return;
      }
    }

    saveConsultationNavigation(filteredConsultationIdsRef.current, consultation.consultationId);
    navigate(`/consultation-detail/${consultation.consultationId}`);
  }, [navigate, addNotification]);

  const columns = useMemo(() => [
    createCustomColumn(
      'patientName',
      'Patient Name',
      (row) => row.nhsId,
      (value) => {
        if (!value) return <span>Unknown</span>;
        if (nhsIdToPatientData.has(value)) {
          const patientData = nhsIdToPatientData.get(value);
          const name = patientData?.name || patientData?.fullName || value;
          return <span>{name}</span>;
        }
        return <span>{value}</span>;
      },
      {
        sortable: true,
        filterable: true
      }
    ),
    createTextColumn(
      'nhsId',
      'NHS Number',
      (row) => {
        if (!row.nhsId) return 'Unknown';
        const nhsId = row.nhsId.replace(/\s/g, '');
        if (nhsId.length === 10) {
          return `${nhsId.slice(0, 3)} ${nhsId.slice(3, 6)} ${nhsId.slice(6, 10)}`;
        }
        return row.nhsId;
      },
      {
        sortable: true,
        filterable: true
      }
    ),
    createCustomColumn(
      'clinicianName',
      'Clinician',
      (row) => row.userId,
      (value) => {
        if (!value) return <span>Unknown</span>;
        if (userIdToClinicianData.has(value)) {
          const clinicianData = userIdToClinicianData.get(value);
          const name = clinicianData?.name || clinicianData?.fullName || clinicianData?.email || value;
          return <span>{name}</span>;
        }
        return <span>{value}</span>;
      },
      {
        sortable: true,
        filterable: true
      }
    ),
    createCustomColumn(
      'status',
      'Status',
      (row) => row.status,
      (value) => {
        const status = ConsultationStatusEnum[value] || ConsultationStatusEnum.PENDING;
        return (
          <StatusBadge
            label={status.description}
            colorFamily={status.colorFamily || 'gray'}
            size="normal"
          />
        );
      },
      {
        sortable: true,
        filterable: true,
        dataType: 'enum',
        enumOptions: Object.values(ConsultationStatusEnum).map(s => s.value),
        compareFn: (a, b) => {
          const statusA = ConsultationStatusEnum[a]?.description || '';
          const statusB = ConsultationStatusEnum[b]?.description || '';
          return statusA.localeCompare(statusB);
        }
      }
    ),
    createDateColumn(
      'timestamp',
      'Created at',
      (row) => row.timestamp,
      {
        sortable: true,
        filterable: true
      }
    ),
    createCustomColumn(
      'actions',
      'Actions',
      (row) => row,
      (value, row) => {
        const { canRegenerate, canDelete } = getRowActions(row);
        return (
          <div className="actions-row">
            <ActionButton
              icon={faRepeat}
              onClick={() => handleRegenerateClick(row)}
              disabled={!canRegenerate}
              title={canRegenerate ? "Regenerate Consultation" : "Cannot regenerate in current state"}
            />
            <ActionButton
              icon={faTrash}
              onClick={() => handleDeleteClick(row)}
              disabled={!canDelete}
              title={canDelete ? "Delete consultation" : "Cannot delete in current state"}
            />
          </div>
        );
      },
      {
        sortable: false,
        filterable: false,
        className: 'actions-column',
        compulsory: true
      }
    )
  ], [nhsIdToPatientData, userIdToClinicianData, getRowActions, handleRegenerateClick, handleDeleteClick]);

  const { columnOrder, hiddenColumns, saveSettings } = useColumnSettings('ScribeListTable', columns);
  const hiddenColumnsSet = useMemo(() => new Set(hiddenColumns), [hiddenColumns]);

  const handleBulkRegenerate = useCallback((selectedRowsData) => {
    if (selectedRowsData.length === 0) return;
    setConsultationsForBulkAction(selectedRowsData);
    setBulkRegenerateModalOpen(true);
  }, []);

  const handleBulkDelete = useCallback((selectedRowsData) => {
    if (selectedRowsData.length === 0) return;
    setConsultationsForBulkAction(selectedRowsData);
    setBulkDeleteModalOpen(true);
  }, []);

  const bulkActions = useMemo(() => [
    {
      key: 'regenerate',
      label: 'Regenerate',
      icon: faRepeat,
      onClick: handleBulkRegenerate
    },
    {
      key: 'delete',
      label: 'Delete',
      icon: faTrash,
      onClick: handleBulkDelete
    }
  ], [handleBulkRegenerate, handleBulkDelete]);

  const closeBulkModal = useCallback(() => {
    setBulkRegenerateModalOpen(false);
    setBulkDeleteModalOpen(false);
    setConsultationsForBulkAction([]);
  }, []);

  const confirmBulkRegenerate = () => {
    if (consultationsForBulkAction.length === 0) return;

    setBulkRegenerateLoading(true);

    const regeneratePromises = consultationsForBulkAction.map(consultation =>
      consultationService.regenerateConsultation(consultation.consultationId)
        .then(() => {
          addNotification(`bulk-regenerate-success-${consultation.consultationId}`, `Queued for regeneration: ${getPatientName(consultation.nhsId)}`);
        })
        .catch(error => {
          console.error(`Error regenerating consultation ${consultation.consultationId}:`, error);
          addNotification(`bulk-regenerate-error-${consultation.consultationId}`, `Failed to regenerate consultation for ${getPatientName(consultation.nhsId)}`, { isError: true });
        })
    );

    Promise.allSettled(regeneratePromises).then(() => {
      setSelectedRows(new Set());
      setBulkRegenerateModalOpen(false);
      setConsultationsForBulkAction([]);
    }).finally(() => {
      setBulkRegenerateLoading(false);
    });
  };

  const confirmBulkDelete = () => {
    if (consultationsForBulkAction.length === 0) return;

    const deletePromises = consultationsForBulkAction.map(consultation =>
      consultationService.deleteConsultation(consultation.consultationId)
        .catch(error => {
          console.error(`Error deleting consultation ${consultation.consultationId}:`, error);
          addNotification(`bulk-delete-error-${consultation.consultationId}`, `Failed to delete consultation for ${getPatientName(consultation.nhsId)}`, { isError: true });
        })
    );

    Promise.allSettled(deletePromises).then(() => {
      // Refresh the consultation list after deletions
      refreshConsultations();
      setSelectedRows(new Set());
      setBulkDeleteModalOpen(false);
      setConsultationsForBulkAction([]);
    });
  };

  return {
    columns,
    columnOrder,
    hiddenColumnsSet,
    saveSettings,
    columnCustomizationOpen,
    setColumnCustomizationOpen,
    sortState,
    setSortState,
    filterValues,
    setFilterValues,
    disabledFilters,
    setDisabledFilters,
    paginationState,
    setPaginationState,
    highlightedRows,
    setHighlightedRows,
    handleFilteredSortedDataChange,
    openConsultation,
    getPatientName,
    selectedRows,
    setSelectedRows,
    bulkActions,
    consultationsForBulkAction,
    bulkDeleteModalOpen,
    bulkRegenerateModalOpen,
    bulkRegenerateLoading,
    closeBulkModal,
    confirmBulkRegenerate,
    confirmBulkDelete,
  };
};

export default useScribeTable;
